import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

const C = {
  gold: '#FACF47', goldLight: '#FEF2B5',
  aqua: '#3DAB9E', coral: '#E17B21',
  purple: '#937ADB', brown: '#4D3F37',
  brownLight: '#4D3F37CC',
};

const AWARDS = [
  { key: 'good_try',      label: 'Good Try',      icon: '🌱', color: C.aqua,   tint: '#21CDBD29' },
  { key: 'great_effort',  label: 'Great Effort',  icon: '💪', color: C.coral,  tint: '#FF854B29' },
  { key: 'good_thinking', label: 'Good Thinking', icon: '💡', color: C.gold,   tint: C.goldLight },
  { key: 'focused',       label: 'Focused',       icon: '🎯', color: C.purple, tint: '#937ADB26' },
  { key: 'team_player',   label: 'Team Player',   icon: '🤝', color: C.brown,  tint: '#4D3F3712' },
];

interface SpeedFlashOverlayProps { name: string; solveTime: string; onDone: () => void; }

export function SpeedFlashOverlay({ name, solveTime, onDone }: SpeedFlashOverlayProps) {
  useEffect(() => {
    const t = setTimeout(() => onDone(), 3200);
    return () => clearTimeout(t);
  }, []);

  return (
    <View style={s.overlayFull}>
      <View style={s.speedFlashCard}>
        <Text style={s.speedFlashWhoosh}>⚡ Zooooom! ⚡</Text>
        <Text style={s.speedFlashName}>{name} was fastest!</Text>
        <View style={s.speedFlashTimePill}>
          <Text style={s.speedFlashTime}>🏁 {solveTime}</Text>
        </View>
      </View>
    </View>
  );
}

interface PublicAwardToastProps { studentName: string; awardKey: string; onDone: () => void; duration?: number; }

export function PublicAwardToast({ studentName, awardKey, onDone, duration = 2800 }: PublicAwardToastProps) {
  const award = AWARDS.find(a => a.key === awardKey) ?? AWARDS[0];
  useEffect(() => {
    const t = setTimeout(() => onDone(), duration);
    return () => clearTimeout(t);
  }, []);

  return (
    <View style={s.toastContainer}>
      <View style={[s.toastCard, { borderColor: award.color }]}>
        <View style={[s.toastIcon, { backgroundColor: award.color }]}>
          <Text style={{ fontSize: 22 }}>{award.icon}</Text>
        </View>
        <View style={s.toastBody}>
          <Text style={s.toastBadgeLabel}>🎉 New badge!</Text>
          <Text style={s.toastTitle}>
            {studentName} got <Text style={{ color: award.color }}>{award.label}</Text>!
          </Text>
          <Text style={s.toastSubtitle}>+1 ⭐ from coach</Text>
        </View>
      </View>
    </View>
  );
}

const STREAK_TIERS = [
  { threshold: 7, name: 'LEGENDARY',   color: C.purple, icon: '👑' },
  { threshold: 5, name: 'Unstoppable', color: C.coral,  icon: '⚡' },
  { threshold: 3, name: 'On a Roll',   color: C.gold,   icon: '🔥' },
];

interface StreakFlashOverlayProps { streak: number; onDone: () => void; }

export function StreakFlashOverlay({ streak, onDone }: StreakFlashOverlayProps) {
  const tier = STREAK_TIERS.find(t => streak >= t.threshold);
  useEffect(() => {
    const duration = tier?.name === 'LEGENDARY' ? 2800 : 2200;
    const t = setTimeout(() => onDone(), duration);
    return () => clearTimeout(t);
  }, []);

  if (!tier) return null;

  if (tier.name === 'LEGENDARY') {
    return (
      <View style={[s.overlayFull, { backgroundColor: C.purple }]}>
        <View style={{ alignItems: 'center' }}>
          <Text style={{ fontSize: 64 }}>{tier.icon}</Text>
          <Text style={s.legendaryLabel}>STREAK</Text>
          <Text style={s.legendaryTitle}>LEGENDARY!</Text>
          <View style={s.streakCountPill}>
            <Text style={s.streakCountText}>🔥 {streak} in a row!</Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={s.streakContainer} pointerEvents="none">
      <View style={[s.streakBadge, { backgroundColor: tier.color }]}>
        <Text style={{ fontSize: 28 }}>{tier.icon}</Text>
        <View>
          <Text style={s.streakWhoah}>WHOA!</Text>
          <Text style={s.streakTierName}>
            {tier.name === 'On a Roll' ? 'On a Roll!' : 'Unstoppable!'}
          </Text>
        </View>
        <View style={[s.streakCountPill, { backgroundColor: '#fff' }]}>
          <Text style={[s.streakCountText, { color: tier.color }]}>{streak} in a row 🔥</Text>
        </View>
      </View>
    </View>
  );
}

interface MysteryBonusOverlayProps { onDone: () => void; }

export function MysteryBonusOverlay({ onDone }: MysteryBonusOverlayProps) {
  const [secs, setSecs] = useState(10);
  useEffect(() => {
    if (secs <= 0) { onDone(); return; }
    const t = setTimeout(() => setSecs(prev => prev - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);

  return (
    <View style={[s.overlayFull, { backgroundColor: C.purple }]}>
      <View style={{ alignItems: 'center', padding: 24 }}>
        <Text style={{ fontSize: 72, marginBottom: 4 }}>🎁</Text>
        <Text style={s.mysteryTitle}>✨ Mystery Time! ✨</Text>
        <Text style={s.mysteryBody}>
          Next solve = <Text style={{ color: C.gold }}>3× stars!</Text>
        </Text>
        <View style={s.mysteryCountdown}>
          <Text style={s.mysteryCountdownNum}>{secs}</Text>
        </View>
        <Text style={s.mysteryFooter}>Solve quick to win big! 🚀</Text>
      </View>
    </View>
  );
}

const CELEBRATION_AWARD_META: Record<string, {title: string; subtitle: string; icon: string; color: string}> = {
  top_solver:           {title: 'Top Solver',    subtitle: 'Most correct puzzles',  icon: '🏆', color: C.gold},
  top_performer:        {title: 'Top Performer', subtitle: 'Most stars earned',      icon: '⭐', color: C.gold},
  most_active:          {title: 'Most Active',   subtitle: 'Most attempts made',     icon: '🚀', color: C.aqua},
  great_effort:         {title: 'Great Effort',  subtitle: 'Hardest worker today',   icon: '💪', color: C.coral},
  fastest_finger:       {title: 'Fastest Finger',subtitle: 'Quickest to answer',     icon: '🖐', color: '#E05FD0'},
  good_try_leader:      {title: 'Good Try',       subtitle: 'Most good tries',        icon: '🌱', color: C.aqua},
  great_effort_leader:  {title: 'Great Effort',  subtitle: 'Most effort shown',      icon: '💪', color: C.coral},
  good_thinking_leader: {title: 'Good Thinking', subtitle: 'Best thinker today',     icon: '💡', color: C.gold},
  focused_leader:       {title: 'Most Focused',  subtitle: 'Laser sharp focus',      icon: '🎯', color: C.purple},
  team_player_leader:   {title: 'Team Player',   subtitle: 'Best team spirit',       icon: '🤝', color: '#4D3F37'},
  coach_star_student:   {title: "Coach's Star",  subtitle: 'Picked by your coach',   icon: '⭐', color: C.purple},
  coachs_star:          {title: "Coach's Star",  subtitle: 'Picked by your coach',   icon: '⭐', color: C.purple},
  everyone_earned:      {title: 'Everyone Earned!', subtitle: 'Whole class scored',  icon: '🎉', color: C.brown},
};

interface CelebrationAward { award: string; user_id: number; name: string; }
interface CelebrationOverlayProps {
  role: 'coach' | 'student';
  awards: CelebrationAward[];
  allEarned: boolean;
  coachStarStudentName?: string;
  onDone: () => void;
  isPreview?: boolean;
}

export function CelebrationOverlay({ role, awards, allEarned, coachStarStudentName, onDone, isPreview = false }: CelebrationOverlayProps) {
  const displayAwards: any[] = [];

  awards.forEach(a => {
    if (a.award === 'everyone_earned') return;
    const meta = CELEBRATION_AWARD_META[a.award];
    if (!meta) return;
    const studentName = (a.award === 'coach_star_student' || a.award === 'coachs_star')
      ? (coachStarStudentName ?? a.name ?? '')
      : a.name;
    if (!studentName) return;
    displayAwards.push({key: a.award, ...meta, studentName});
  });

  if (allEarned) {
    displayAwards.push({key: 'everyone_earned', ...CELEBRATION_AWARD_META.everyone_earned, studentName: 'Class'});
  }

  return (
    <View style={[s.overlayFull, { backgroundColor: C.gold, padding: 24 }]}>
      <View style={{ alignItems: 'center', marginBottom: 18 }}>
        <Text style={{ fontSize: 56, marginBottom: 4 }}>{role === 'coach' ? '🏆' : '🎉'}</Text>
        <Text style={s.celebTitle}>
          {role === 'coach' ? "Tonight's Champions!" : 'WOOHOO! Great class!'}
        </Text>
      </View>
      <View style={s.celebCards}>
        {displayAwards.map((a: any) => (
          <View key={a.key} style={[s.celebCard, { borderColor: a.color }]}>
            <Text style={{ fontSize: 24 }}>{a.icon}</Text>
            <Text style={[s.celebCardTitle, { color: a.color }]}>{a.title}</Text>
            <Text style={s.celebCardName}>{a.studentName}</Text>
          </View>
        ))}
      </View>
      <TouchableOpacity onPress={onDone} style={s.celebBtn}>
        <Text style={s.celebBtnText}>Awesome! 🎉</Text>
      </TouchableOpacity>
    </View>
  );
}

export type OverlayKind = 'speed_flash' | 'award_toast' | 'streak' | 'mystery' | 'celebration' | null;

export interface OverlayState {
  kind: OverlayKind;
  props?: Record<string, any>;
}

interface OverlayManagerProps {
  overlay: OverlayState | null;
  onClear: () => void;
  role: 'coach' | 'student';
}

import { SpeedFlashHtml, PublicAwardToastHtml, StreakFlashHtml, MysteryBonusHtml, CelebrationHtml } from './htmlOverlays';

export function OverlayManager({ overlay, onClear, role }: OverlayManagerProps) {
  if (!overlay?.kind) return null;
  const { kind, props = {} } = overlay;
  if (kind === 'speed_flash')
    return <SpeedFlashHtml {...(props as any)} onDone={onClear} />;
  if (kind === 'award_toast')
    return <PublicAwardToastHtml {...(props as any)} onDone={onClear} />;
  if (kind === 'streak')
    return <StreakFlashHtml {...(props as any)} onDone={onClear} />;
  if (kind === 'mystery')
    return <MysteryBonusHtml onDone={onClear} />;
  if (kind === 'celebration')
    return <CelebrationHtml {...(props as any)} role={role} onDone={onClear} />;
  return null;
}

const s = StyleSheet.create({
  overlayFull: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, alignItems: 'center', justifyContent: 'center' },
  speedFlashCard: { padding: 24, backgroundColor: '#FFD84A', borderRadius: 24, alignItems: 'center', minWidth: 260 },
  speedFlashWhoosh: { fontSize: 14, fontWeight: '900', color: '#fff', letterSpacing: 3, textTransform: 'uppercase' },
  speedFlashName: { fontSize: 26, fontWeight: '900', color: '#fff', marginTop: 4 },
  speedFlashTimePill: { marginTop: 10, backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 999, paddingHorizontal: 14, paddingVertical: 4 },
  speedFlashTime: { fontSize: 14, fontWeight: '800', color: '#E17846' },
  toastContainer: { position: 'absolute', top: 14, left: '10%', right: '10%', zIndex: 45 },
  toastCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, backgroundColor: '#fff', borderWidth: 3, borderRadius: 18 },
  toastIcon: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  toastBody: { flex: 1 },
  toastBadgeLabel: { fontSize: 11, fontWeight: '800', color: '#3DAB9E', textTransform: 'uppercase', letterSpacing: 1.5 },
  toastTitle: { fontSize: 16, fontWeight: '900', color: '#4D3F37' },
  toastSubtitle: { fontSize: 12, fontWeight: '700', color: '#4D3F37CC' },
  legendaryLabel: { fontSize: 16, fontWeight: '900', color: '#fff', letterSpacing: 5, marginTop: 6 },
  legendaryTitle: { fontSize: 60, fontWeight: '900', color: '#fff', letterSpacing: 8 },
  streakCountPill: { borderRadius: 999, paddingHorizontal: 12, paddingVertical: 4, marginTop: 10 },
  streakCountText: { fontSize: 14, fontWeight: '900' },
  streakContainer: { position: 'absolute', top: 0, left: 0, right: 0, paddingTop: 50, alignItems: 'center', zIndex: 60 },
  streakBadge: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 22, paddingVertical: 14, borderRadius: 999, borderWidth: 4, borderColor: '#fff' },
  streakWhoah: { fontSize: 10, fontWeight: '900', color: 'rgba(255,255,255,0.9)', letterSpacing: 2 },
  streakTierName: { fontSize: 20, fontWeight: '900', color: '#fff', marginTop: 2 },
  mysteryTitle: { fontSize: 16, fontWeight: '900', color: '#fff', letterSpacing: 5, textTransform: 'uppercase', marginBottom: 6 },
  mysteryBody: { fontSize: 28, fontWeight: '900', color: '#fff', marginBottom: 14, textAlign: 'center' },
  mysteryCountdown: { width: 100, height: 100, borderRadius: 50, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  mysteryCountdownNum: { fontSize: 44, fontWeight: '900', color: '#fff' },
  mysteryFooter: { fontSize: 15, fontWeight: '800', color: '#fff' },
  celebTitle: { fontSize: 32, fontWeight: '900', color: '#fff', textAlign: 'center' },
  celebCards: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12 },
  celebCard: { padding: 14, backgroundColor: '#fff', borderRadius: 16, borderWidth: 3, minWidth: 150, alignItems: 'center' },
  celebCardTitle: { fontSize: 11, fontWeight: '900', letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 },
  celebCardName: { fontSize: 18, fontWeight: '900', color: '#4D3F37', marginTop: 4 },
  celebBtn: { marginTop: 20, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 12, backgroundColor: '#fff' },
  celebBtnText: { fontSize: 14, fontWeight: '900', color: '#E17846' },
});
